import React, {useEffect, useMemo, useState} from 'react';
import {Button, Modal, Typography, Space, message as antdMessage, message} from 'antd';
import partnerService from '../../../services/partnerService';
import {bestPartnersTableColumns} from '../../../components/molecules/Table/columns';
import TableComponent from '../../../components/molecules/Table/component';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import productService from '../../../services/productService';
import {handleLogout, createUserTableChangeHandler, useIsMobile} from '../../../utils/helpers';
import {useNavigate} from 'react-router-dom';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {mobileRenderers} from "../../../components/molecules/Table/tableMobileRenderers";

const {confirm, success} = Modal;
const {Paragraph, Text} = Typography;

const BestPartnersPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const isMobile = useIsMobile();

    const [partnerList, setPartnerList] = useState<any[]>([]);
    const currentRole = currentUser?.roles?.find((r: any) => r.roleType === role);
    const showBecomePartner = role === 'advertiser' && currentUser.roles.length === 1;
    const showUpgradeToPartner = role === "promoter";

    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        userType: '',
        search: '',
        sortBy: '',
        sortDirection: 'desc' as 'asc' | 'desc',
    });

    const onTableChange = useMemo(() =>
        createUserTableChangeHandler(filters, setFilters), [filters]
    );

    const onLogout = () => {
        handleLogout(dispatch, navigate)();
    };

    useEffect(() => {
        const fetchPartners = async () => {
            setLoading(true);
            try {
                const data = await partnerService.getBestPartners(filters);
                setPartnerList(data);
            } catch (error) {
                console.error('Error fetching partners:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPartners();
    }, [filters]);

    const handleRowClick = (record: any) => {
        navigate(`/partners/${record._id}`);
    };

    const handleBecomePartner = async () => {
        confirm({
            title: 'Add Partner Role',
            icon: <ExclamationCircleOutlined/>,
            content: (
                <>
                    By confirming, you’ll add the <b>Partner</b> role to your account.
                    You can explore the new Partner Portal right away, and—when you’re ready
                    to actually onboard new users, create affiliate links and get paid for each user and click you
                    bring—you’ll find instructions there on activating your Partner subscription.
                </>
            ),
            okText: 'Confirm',
            cancelText: 'Cancel',
            async onOk() {
                try {
                    const res = await partnerService.becomePartner();
                    if (res.status === 200) {
                        success({
                            title: 'Success!',
                            content: (
                                <>
                                    Your account now has both {role} and partner roles.
                                    To apply this change, please log out and then log back in.
                                </>
                            ),
                            okText: 'Log out',
                            onOk: onLogout
                        });
                    } else {
                        message.error('Error while adding partner role.');
                    }
                } catch (err: any) {
                    message.error(err.message || 'Error while adding partner role.');
                }
            }
        });
    };

    const handleUpgradeToPartner = () => {
        if (currentRole?.isActive) {
            confirm({
                title: 'Upgrade from Promoter to Partner Subscription',
                icon: <ExclamationCircleOutlined/>,
                content: (
                    <Space direction="vertical" size="middle" style={{width: '100%'}}>
                        <Paragraph>
                            By upgrading, you’ll convert your <Text strong>Promoter</Text> role into a full
                            <Text strong> Partner</Text> account, while keeping all of your existing data intact:
                        </Paragraph>
                        <ul style={{paddingLeft: 20, margin: 0, listStyleType: 'disc'}}>
                            <li>Affiliate links</li>
                            <li>Wallet balances</li>
                            <li>Transaction history</li>
                        </ul>
                        <Paragraph>
                            You’ll still enjoy every feature you have as a promoter, <Text strong>plus</Text> access
                            to partner programs and referral commissions. Click <Text strong>Proceed</Text> to open
                            your billing portal and select the Partner plan that’s right for you.
                        </Paragraph>
                    </Space>
                ),
                okText: 'Proceed',
                cancelText: 'Cancel',
                onOk: async () => {
                    try {
                        const response = await productService.createPortalSession({action: 'upgrade_to_partner'});
                        if (response?.data?.url) {
                            window.location.href = response.data.url;
                        } else {
                            antdMessage.error('No portal URL returned');
                        }
                    } catch (err: any) {
                        antdMessage.error(err.message || 'Failed to open billing portal.');
                    }
                }
            });
        } else {
            confirm({
                title: 'Upgrade from Promoter to Partner Role',
                icon: <ExclamationCircleOutlined/>,
                content: (
                    <Space direction="vertical" size="middle" style={{width: '100%'}}>
                        <Paragraph>
                            You currently have a Promoter role but no active subscription. With one click you can:
                        </Paragraph>
                        <ul style={{paddingLeft: 20, margin: 0, listStyleType: 'disc'}}>
                            <li>Switch your role to Partner immediately.</li>
                            <li>Explore the Partner Portal right away.</li>
                            <li>Follow step-by-step guidance there to activate your Partner subscription when you’re
                                ready to earn referral fees.
                            </li>
                        </ul>
                    </Space>
                ),
                okText: 'Upgrade',
                cancelText: 'Cancel',
                onOk: async () => {
                    try {
                        const response = await partnerService.upgradeToPartner();
                        if (response.status === 200) {
                            Modal.success({
                                title: 'Role Upgrade Successful',
                                content: 'Your account is now a Partner. Please log out and back in to apply this change.',
                                okText: 'Log out',
                                onOk: onLogout
                            });
                        } else {
                            message.error(response.data?.message || 'Failed to upgrade role.');
                        }
                    } catch (err: any) {
                        antdMessage.error(err.message || 'Failed to upgrade role.');
                    }
                }
            });
        }
    };

    return (
        <div style={{position: 'relative', paddingTop: isMobile ? (showBecomePartner || showUpgradeToPartner ? '50px' : '40px') : '20px'}}>
            {showBecomePartner && (
                <div style={{position: 'absolute', top: 0, right: 0, margin: '10px'}}>
                    <Button
                        type="primary"
                        size={isMobile ? 'small' : 'middle'}
                        style={isMobile ? {fontSize: 12, padding: '0 10px', height: 28} : undefined}
                        onClick={handleBecomePartner}
                    >
                        Become a partner
                    </Button>
                </div>
            )}
            {showUpgradeToPartner && (
                <div style={{position: 'absolute', top: 0, right: 0, margin: '10px'}}>
                    <Button
                        type="primary"
                        size={isMobile ? 'small' : 'middle'}
                        style={isMobile ? {fontSize: 12, padding: '0 10px', height: 28} : undefined}
                        onClick={handleUpgradeToPartner}
                    >
                        Upgrade to partner
                    </Button>
                </div>
            )}
            <TableComponent columns={bestPartnersTableColumns} title={"Best partners"} data={partnerList || []}
                            onRowClick={handleRowClick} loading={loading} onChange={onTableChange} isMobile={isMobile}
                            mobileVariant="bestPartners" mobileRenderers={mobileRenderers}
                            rowKey={(row) => row._id}
            />
        </div>
    );
};

export default BestPartnersPage;
